import React, { useState, useEffect } from 'react';
import { Card, List, Tag, Badge, Empty, Spin, Button } from 'antd';
import { NotificationOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { faultReportService } from '../../services/api/fault-report.service';
import { FaultReportNotification } from '../../types/faultReport';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const FaultNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<FaultReportNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await faultReportService.getNotifications();
      setNotifications(response.data.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await faultReportService.markNotificationAsRead(id);
      setNotifications(prevNotifications =>
        prevNotifications.map(notif =>
          notif.id === id ? { ...notif, read_at: new Date().toISOString() } : notif
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      pending: { color: 'warning', icon: <ClockCircleOutlined />, text: 'Na čekanju' },
      in_progress: { color: 'processing', icon: <ExclamationCircleOutlined />, text: 'U obradi' },
      resolved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Riješeno' }
    };
    const config = statusConfig[status as keyof typeof statusConfig];
    return <Tag icon={config.icon} color={config.color}>{config.text}</Tag>;
  };

  const handleNotificationClick = (notification: FaultReportNotification) => {
    if (!notification.read_at) {
      handleMarkAsRead(notification.id);
    }
    navigate(`/issues/${notification.data.fault_report_id}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card 
      title={
        <div className="flex items-center">
          <NotificationOutlined className="mr-2" />
          <span>Obavještenja o prijavama</span>
        </div>
      }
      extra={
        <Button type="link" onClick={() => navigate('/issues')}>
          Pregledaj sve prijave
        </Button>
      }
    >
      {notifications.length > 0 ? (
        <List
          itemLayout="horizontal"
          dataSource={notifications}
          renderItem={notification => (
            <List.Item
              className={`cursor-pointer transition-colors ${!notification.read_at ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}`}
              onClick={() => handleNotificationClick(notification)}
              actions={[
                getStatusTag(notification.data.status),
                !notification.read_at && (
                  <Button 
                    type="link" 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsRead(notification.id);
                    }}
                  >
                    Označi kao pročitano
                  </Button>
                )
              ]}
            >
              <List.Item.Meta
                title={
                  <div className="flex items-center">
                    {!notification.read_at && (
                      <Badge status="processing" className="mr-2" />
                    )}
                    {notification.data.title}
                  </div>
                }
                description={
                  <div>
                    <p>{notification.data.content}</p>
                    <small className="text-gray-500">
                      {dayjs(notification.created_at).format('DD.MM.YYYY HH:mm')}
                    </small>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nema novih obavještenja"
        />
      )}
    </Card>
  );
};

export default FaultNotifications;